import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getTaxiCopy } from './taxi-i18n';
import { TAXI_THEME } from './taxi-theme';
import useTaxiSeo from './useTaxiSeo';
import TaxiLanguageSelector from './TaxiLanguageSelector';
import TaxiLogo from './TaxiLogo';
import {
  LandmarkIcon,
  LocationIcon,
  PhoneIcon,
  ServiceIcon,
  WhatsAppIcon,
} from './TaxiIcons';

gsap.registerPlugin(ScrollTrigger);

export default function TaxiLanding() {
  const { lang } = useParams();
  const copy = getTaxiCopy(lang);
  const pageRef = useRef(null);
  useTaxiSeo(copy.lang);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.taxi-hero-content > *', {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        delay: 0.2,
      });

      gsap.utils.toArray('.taxi-animate').forEach((el) => {
        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
          y: 36,
          opacity: 0,
          duration: 0.8,
          ease: 'power2.out',
        });
      });

      gsap.utils.toArray('.taxi-card').forEach((el, i) => {
        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
          y: 28,
          opacity: 0,
          duration: 0.7,
          delay: i * 0.08,
          ease: 'power2.out',
        });
      });
    }, pageRef);

    return () => ctx.revert();
  }, [copy.lang]);

  const aboutLead = copy.aboutLead.replace('{vehicle}', TAXI_THEME.vehicle);

  return (
    <div className="taxi-page" ref={pageRef} lang={copy.lang}>
      <TaxiLanguageSelector label={copy.langLabel} />

      <header className="taxi-hero" role="banner">
        <video
          className="taxi-hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/images/taxi-radenovic-logo.svg"
          aria-hidden="true"
        >
          <source src="/videos/taxi-hero.mp4" type="video/mp4" />
        </video>
        <div className="taxi-hero-overlay" aria-hidden="true" />
        <div className="taxi-hero-content">
          <TaxiLogo subtitle={copy.brandSubtitle} />
          <span className="taxi-badge">TAXI</span>
          <h1 className="taxi-hero-title">{copy.tagline}</h1>
          <p className="taxi-hero-lead">{copy.heroLead}</p>
          <div className="taxi-hero-actions">
            <a href={`tel:${TAXI_THEME.phone}`} className="taxi-btn taxi-btn--primary">
              <PhoneIcon />
              {TAXI_THEME.phoneDisplay}
            </a>
            <a
              href={`https://wa.me/${TAXI_THEME.phone.replace('+', '')}`}
              className="taxi-btn taxi-btn--ghost"
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsAppIcon />
              WhatsApp
            </a>
          </div>
        </div>
        <div className="taxi-hero-scroll" aria-hidden="true">
          <span>{copy.scroll}</span>
        </div>
      </header>

      <main>
        <section className="taxi-section taxi-services" aria-labelledby="taxi-services-title">
          <div className="taxi-container">
            <p className="taxi-section-eyebrow taxi-animate">{copy.serviceTitle}</p>
            <h2 id="taxi-services-title" className="taxi-section-title taxi-animate">
              {copy.servicesTitle}
            </h2>
            <div className="taxi-services-grid">
              {copy.services.map((service) => (
                <article key={service.id} className="taxi-service-card taxi-card">
                  <div className="taxi-service-icon">
                    <ServiceIcon type={service.icon} />
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="taxi-section taxi-destinations" aria-labelledby="taxi-destinations-title">
          <div className="taxi-container">
            <p className="taxi-section-eyebrow taxi-animate">{copy.destinationsEyebrow}</p>
            <h2 id="taxi-destinations-title" className="taxi-section-title taxi-animate">
              {copy.destinationsTitle}
            </h2>
            <p className="taxi-section-lead taxi-animate">{copy.destinationsLead}</p>
            <div className="taxi-destinations-grid">
              {copy.destinations.map((dest) => (
                <article key={dest.id} className="taxi-destination-card taxi-card">
                  <div className="taxi-destination-icon">
                    <LandmarkIcon type={dest.landmark} />
                  </div>
                  <div className="taxi-destination-meta">
                    <span className="taxi-destination-duration">{dest.duration}</span>
                    <span className="taxi-destination-country">{dest.country}</span>
                  </div>
                  <h3>{dest.name}</h3>
                  <p>{dest.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="taxi-section taxi-about" aria-labelledby="taxi-about-title">
          <div className="taxi-container taxi-about-inner">
            <div className="taxi-about-copy taxi-animate">
              <p className="taxi-section-eyebrow">{copy.serviceTitle}</p>
              <h2 id="taxi-about-title" className="taxi-section-title">
                {copy.aboutTitle}
              </h2>
              <p className="taxi-section-lead">{aboutLead}</p>
              <ul className="taxi-values">
                {copy.values.map((value) => (
                  <li key={value}>{value}</li>
                ))}
              </ul>
            </div>
            <div className="taxi-about-visual taxi-animate">
              <div className="taxi-car-photo-wrap">
                <img
                  src="/images/taxi-car.jpg"
                  alt={copy.carAlt}
                  className="taxi-car-photo"
                />
                <div className="taxi-car-overlay" aria-hidden="true" />
              </div>
              <p className="taxi-vehicle-label">{TAXI_THEME.vehicle}</p>
            </div>
          </div>
        </section>

        <section className="taxi-section taxi-contact" aria-labelledby="taxi-contact-title">
          <div className="taxi-container taxi-contact-inner taxi-animate">
            <h2 id="taxi-contact-title" className="taxi-contact-title">
              {copy.contactTitle}
            </h2>
            <p className="taxi-contact-lead">{copy.contactLead}</p>
            <a href={`tel:${TAXI_THEME.phone}`} className="taxi-contact-phone">
              <PhoneIcon />
              {TAXI_THEME.phoneDisplay}
            </a>
            <p className="taxi-contact-location">
              <LocationIcon />
              {TAXI_THEME.location}
            </p>
          </div>
        </section>
      </main>

      <footer className="taxi-footer">
        <div className="taxi-container taxi-footer-inner">
          <TaxiLogo compact subtitle={copy.brandSubtitle} />
          <p>
            &copy; {new Date().getFullYear()} Taxi Radenović — {copy.brandSubtitle}. {copy.footerNote}
          </p>
        </div>
      </footer>
    </div>
  );
}
